import {alertAppearance} from '../priceAlerts/appearance.js'
import {applyOpacityToColor} from './computeIndicator.js'

// One stable overlay per chart. Native paint owns all pan/zoom coordinates:
// no timeline data, autoscale contribution, animation loop, or React hover state.
export class PriceAlertPrimitive {
 constructor(container,onDelete,onSelect,onMove) {
  this.container=container;this.onDelete=onDelete;this.onSelect=onSelect;this.onMove=onMove
  this.alerts=[];this.appearance=alertAppearance();this.hovered=null;this.dragging=false;this.height=0;this.width=0
  const renderer={draw:target=>this.draw(target)}
  this.views=[{zOrder:()=> 'top',renderer:()=>renderer}]
  this.label=document.createElement('div');this.label.dataset.priceAlertLabel='';this.label.hidden=true
  this.label.className='absolute z-30 flex max-w-[85%] items-center gap-1 rounded border border-border bg-canvas px-1 text-[11px] text-inkMuted shadow-sm'
  this.label.style.cssText='display:none;height:22px;pointer-events:auto;transform:translate(-50%,-50%)'
  this.name=document.createElement('button');this.name.type='button';this.name.className='truncate px-1';this.name.ondblclick=()=>this.hovered&&this.onSelect(this.hovered.id);this.name.onclick=e=>{if(e.detail===0&&this.hovered)this.onSelect(this.hovered.id)}
  this.trash=document.createElement('button');this.trash.type='button';this.trash.className='shrink-0 rounded p-0.5 text-red-500 hover:text-red-600'
  this.trash.innerHTML='<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true"><path d="M3 6h18M9 6V4h6v2M5 6l1 14h12l1-14M10 10v6M14 10v6"/></svg>'
  this.trash.onclick=()=>{if(this.hovered&&this.canDelete&&!this.drag){this.onDelete(this.hovered);this.hide()}}
  this.label.append(this.name,this.trash);container.append(this.label)
  this.label.onpointerenter=()=>{this.overLabel=true}
  this.label.onpointerleave=()=>{this.overLabel=false;if(!this.drag)this.hide()}
  this.stop=e=>e.stopPropagation();for(const event of ['pointerdown','mousedown','click','dblclick','contextmenu'])this.label.addEventListener(event,this.stop)
  this.move=e=>{
   if(this.drag){this.positionLabel();return}
   if(this.overLabel)return
   if(this.dragging||(!this.press&&e.sourceEvent?.buttons)){this.hide();return}
   if(this.press||!e.sourceEvent)return
   if(!e.sourceEvent||!e.point||(e.paneIndex!=null&&e.paneIndex!==0))return this.hide()
   const {x,y}=e.point
   if(x<0||x>this.width||y<0||y>this.height)return this.hide()
   let nearest=null,distance=6
   for(const alert of this.alerts){const coord=this.series?.priceToCoordinate(alert.price);if(Number.isFinite(coord)&&Math.abs(coord-y)<distance){nearest=alert;distance=Math.abs(coord-y)}}
   if(!nearest)return this.hide()
   this.hovered=nearest;this.positionLabel()
  }
  this.down=e=>{
   if(this.label.contains(e.target))return
   if(this.drag){if(e.metaKey)this.block(e);return}
   if(e.button===0&&e.metaKey&&this.live&&this.onMove&&!this.label.contains(e.target)){
    const {x,y}=this.point(e)
    const alert=this.nearest(x,y)
    if(alert){
     this.block(e);this.hovered=alert
     this.drag={alert,price:alert.price,pointerId:e.pointerId,buttonDown:true,commandDown:true,
      offset:y-this.series.priceToCoordinate(alert.price),startY:y,phase:'moving'}
     this.positionLabel()
     this.oldCursor=this.container.style.cursor;this.container.style.cursor='grabbing'
     try{this.container.setPointerCapture(e.pointerId)}catch{/* Synthetic event or released pointer. */}
     return
    }
   }
   if(e.button===0){
    this.press={x:e.clientX,y:e.clientY,pointerId:e.pointerId}
    const {x,y}=this.point(e),alert=this.nearest(x,y)
    if(alert){this.hovered=alert;this.positionLabel()}else this.hide()
   }
  }
  this.pointerMove=e=>{
   if(this.press&&e.pointerId===this.press.pointerId&&Math.hypot(e.clientX-this.press.x,e.clientY-this.press.y)>3){this.dragging=true;this.hide()}
   if(!this.drag||this.drag.phase!=='moving'||e.pointerId!==this.drag.pointerId)return
   this.block(e)
   if(this.drag.buttonDown)this.preview(e)
  }
  this.up=e=>{
   this.dragging=false;this.press=null
   if(!this.drag||this.drag.phase!=='moving'||e.pointerId!==this.drag.pointerId||e.button!==0)return
   this.block(e)
   if(this.drag.buttonDown)this.preview(e)
   this.drag.buttonDown=false;this.drag.commandDown=e.metaKey
   this.releaseCapture();this.finishDrag()
  }
  this.keyUp=e=>{if(this.drag?.phase==='moving'&&e.key==='Meta'&&!e.metaKey){this.drag.commandDown=false;this.finishDrag()}}
  this.keyDown=e=>{if(this.drag?.phase==='moving'&&e.key==='Escape'){this.block(e);this.cancelDrag()}}
  this.cancel=()=>{this.dragging=false;this.press=null;if(this.drag?.phase==='moving')this.cancelDrag()}
  this.visibility=()=>{if(document.hidden)this.cancel()}
  this.doubleClick=e=>{
   if(this.drag||e.button!==0||e.metaKey||e.ctrlKey||e.altKey||e.shiftKey||this.label.contains(e.target))return
   const {x,y}=this.point(e),alert=this.nearest(x,y)
   if(alert){this.block(e);this.hovered=alert;this.positionLabel();this.onSelect(alert.id)}
  }
  this.compatMouse=e=>{
   if(this.drag?.phase==='moving'){this.block(e);return}
   if(e.detail===2&&!e.metaKey&&!e.ctrlKey&&!e.altKey&&!e.shiftKey&&!this.label.contains(e.target)){
    const {x,y}=this.point(e);if(this.nearest(x,y))this.block(e)
   }
  }
  this.leave=()=>{if(!this.drag)this.hide()};this.wheel=()=>{this.hide();this.cancel()}
  container.addEventListener('pointerdown',this.down,true);container.addEventListener('mousedown',this.compatMouse,true)
  container.addEventListener('dblclick',this.doubleClick,true)
  container.addEventListener('pointerleave',this.leave);container.addEventListener('wheel',this.wheel,{passive:true})
  window.addEventListener('pointermove',this.pointerMove,true);window.addEventListener('pointerup',this.up,true)
  window.addEventListener('pointercancel',this.cancel);window.addEventListener('blur',this.cancel)
  window.addEventListener('keydown',this.keyDown,true);window.addEventListener('keyup',this.keyUp,true)
  document.addEventListener('visibilitychange',this.visibility)
 }
 attached({chart,series,requestUpdate}){this.chart=chart;this.series=series;this.requestUpdate=requestUpdate;chart.subscribeCrosshairMove(this.move)}
 detached(){
  this.cancelDrag();this.chart?.unsubscribeCrosshairMove(this.move)
  this.container.removeEventListener('pointerdown',this.down,true);this.container.removeEventListener('mousedown',this.compatMouse,true)
  this.container.removeEventListener('dblclick',this.doubleClick,true)
  this.container.removeEventListener('pointerleave',this.leave);this.container.removeEventListener('wheel',this.wheel)
  window.removeEventListener('pointermove',this.pointerMove,true);window.removeEventListener('pointerup',this.up,true)
  window.removeEventListener('pointercancel',this.cancel);window.removeEventListener('blur',this.cancel)
  window.removeEventListener('keydown',this.keyDown,true);window.removeEventListener('keyup',this.keyUp,true)
  document.removeEventListener('visibilitychange',this.visibility)
  this.label.remove();this.chart=null;this.series=null;this.requestUpdate=null
 }
 block(e){e.preventDefault();e.stopImmediatePropagation()}
 point(e){
  const rect=(this.chart?.panes?.()[0]?.getHTMLElement?.()||this.container).getBoundingClientRect()
  return {x:e.clientX-rect.left,y:e.clientY-rect.top}
 }
 nearest(x,y){
  if(x<0||x>this.width||y<0||y>this.height)return null
  let nearest=null,distance=6
  for(const alert of this.alerts){
   const coordinate=this.series?.priceToCoordinate(alert.price)
   if(Number.isFinite(coordinate)&&Math.abs(coordinate-y)<distance){nearest=alert;distance=Math.abs(coordinate-y)}
  }
  return nearest
 }
 preview(e){
  const drag=this.drag,{y}=this.point(e)
  if(Math.abs(y-drag.startY)<1){
   if(drag.price!==drag.alert.price){drag.price=drag.alert.price;this.requestUpdate?.()}
   return
  }
  const value=this.series?.coordinateToPrice(Math.max(0,Math.min(this.height,y-drag.offset)))
  const precision=Math.min(8,Math.max(0,this.series?.options?.().priceFormat?.precision??2))
  const price=Number(value?.toFixed(precision))
  if(!Number.isFinite(price)||price<=0||price>=100000000||price===drag.price)return
  drag.price=price;this.positionLabel();this.requestUpdate?.()
 }
 releaseCapture(){try{if(this.drag)this.container.releasePointerCapture(this.drag.pointerId)}catch{/* No active capture. */}}
 cancelDrag(){
  if(!this.drag)return
  this.releaseCapture();this.drag=null;this.container.style.cursor=this.oldCursor||''
  this.positionLabel()
  this.requestUpdate?.()
 }
 finishDrag(){
  const drag=this.drag
  if(!drag||drag.phase!=='moving'||drag.buttonDown||drag.commandDown)return
  if(drag.price===drag.alert.price){this.cancelDrag();return}
  drag.phase='pending';this.container.style.cursor=this.oldCursor||''
  // One revision-guarded command after both releases. SSE remains the only
  // writer of saved rules; keep the local preview until acknowledgement.
  try{
   Promise.resolve(this.onMove(drag.alert,drag.price)).then(saved=>{
    if(!saved&&this.drag===drag)this.cancelDrag()
   },()=>{if(this.drag===drag)this.cancelDrag()})
  }catch{this.cancelDrag()}
 }

 paneViews(){return this.views}
 updateAllViews(){}
 setAlerts(alerts,settings,live,canDelete=live){
  const appearance=alertAppearance(settings),key=JSON.stringify([alerts.map(a=>[a.id,a.revision,a.price,a.name,a.state,a.enabled]),appearance,live,canDelete])
  if(this.drag){
   const current=alerts.find(a=>a.id===this.drag.alert.id)
   if(!live||!current||current.revision!==this.drag.alert.revision||current.state!==this.drag.alert.state||current.enabled!==this.drag.alert.enabled)this.cancelDrag()
  }
  if(key===this.key)return
  this.key=key;this.alerts=alerts;this.appearance=appearance;this.live=live;this.canDelete=canDelete
  if(this.hovered)this.hovered=alerts.find(a=>a.id===this.hovered.id)||null
  this.positionLabel();this.requestUpdate?.()
 }
 hide(){this.hovered=null;if(!this.label.hidden){this.label.hidden=true;this.label.style.display='none'}}
 positionLabel(){
  const y=this.hovered&&this.series?.priceToCoordinate(this.drag?.alert.id===this.hovered.id?this.drag.price:this.hovered.price)
  if(!this.hovered||!Number.isFinite(y)||y<11||y>this.height-11||this.dragging){if(!this.label.hidden){this.label.hidden=true;this.label.style.display='none'}return}
  if(this.name.textContent!==this.hovered.name)this.name.textContent=this.hovered.name
  const title=`Delete ${this.hovered.name}`
  if(this.trash.getAttribute('aria-label')!==title)this.trash.setAttribute('aria-label',title)
  this.trash.disabled=!this.canDelete||Boolean(this.drag)
  const left=`${this.width/2}px`,top=`${y}px`
  if(this.label.style.left!==left)this.label.style.left=left
  if(this.label.style.top!==top)this.label.style.top=top
  if(this.label.hidden){this.label.hidden=false;this.label.style.display='flex'}
 }
 draw(target){
  target.useMediaCoordinateSpace(({context:ctx,mediaSize})=>{
   this.width=mediaSize.width;this.height=mediaSize.height
   const {color,opacity,width,style}=this.appearance
   ctx.save();ctx.strokeStyle=applyOpacityToColor(color,opacity);ctx.lineWidth=width;ctx.setLineDash(style===0?[]:style===1?[1,3]:[4,5])
   ctx.beginPath()
   for(const alert of this.alerts){const y=this.series?.priceToCoordinate(this.drag?.alert.id===alert.id?this.drag.price:alert.price);if(!Number.isFinite(y)||y<0||y>mediaSize.height)continue;ctx.moveTo(0,y);ctx.lineTo(mediaSize.width,y)}
   ctx.stroke();ctx.restore()
  })
  this.positionLabel()
 }
}
